import React, { useState, createContext, useContext, Component } from 'react';
// ─── Theme Token Types ────────────────────────────────────────────────────────
interface Theme {
  pageBg: string;
  sidebarBg: string;
  surface: string;
  card: string;
  deepCard: string;
  inputBg: string;
  green: string;
  greenDim: string;
  greenFaint: string;
  greenGhost: string;
  white: string;
  textSecondary: string;
  textMuted: string;
  textLabel: string;
  border: string;
  borderCard: string;
  warning: string;
  error: string;
}
const DARK_THEME: Theme = {
  pageBg: '#111111',
  sidebarBg: '#0a0a0a',
  surface: '#1a1a1a',
  card: '#1f1f1f',
  deepCard: '#0f0f0f',
  inputBg: '#161616',
  green: '#7DC242',
  greenDim: '#6aad35',
  greenFaint: 'rgba(125,194,66,0.06)',
  greenGhost: 'rgba(125,194,66,0.12)',
  white: '#f0f0f0',
  textSecondary: '#5a5a5a',
  textMuted: '#3a3a3a',
  textLabel: '#4a4a4a',
  border: '#252525',
  borderCard: '#1f1f1f',
  warning: '#f0a030',
  error: '#e05252'
};
const LIGHT_THEME: Theme = {
  pageBg: '#E8EDF5',
  sidebarBg: '#ffffff',
  surface: '#e0e5ee',
  card: '#dce1ea',
  deepCard: '#f5f7fa',
  inputBg: '#ffffff',
  green: '#7B2D26',
  greenDim: '#632420',
  greenFaint: 'rgba(123,45,38,0.06)',
  greenGhost: 'rgba(123,45,38,0.12)',
  white: '#1a1a2e',
  textSecondary: '#4a5568',
  textMuted: '#a0aec0',
  textLabel: '#5a6577',
  border: '#d2d8e3',
  borderCard: '#dce1ea',
  warning: '#c47a10',
  error: '#c04040'
};
const ThemeContext = createContext<Theme>(DARK_THEME);
// ─── Step Definitions ─────────────────────────────────────────────────────────
const STEPS = [{
  id: 1,
  label: 'Loan Pricer',
  short: 'Pricer'
}, {
  id: 2,
  label: 'I.P.A.C.T',
  short: 'I.P.A.C.T'
}];
// ─── Conditions Data ──────────────────────────────────────────────────────────
type DocStatus = 'uploaded' | 'pending' | 'na';
interface Condition {
  id: string;
  name: string;
  required: boolean;
  status: DocStatus;
}
const INITIAL_CONDITIONS: Condition[] = [{
  id: '1',
  name: 'Signed 1003 Loan Application',
  required: true,
  status: 'uploaded'
}, {
  id: '2',
  name: 'Credit Report Authorization',
  required: true,
  status: 'uploaded'
}, {
  id: '3',
  name: 'Pay Stubs — Most Recent 30 Days',
  required: true,
  status: 'uploaded'
}, {
  id: '4',
  name: 'W-2s — 2 Years',
  required: true,
  status: 'uploaded'
}, {
  id: '5',
  name: 'Federal Tax Returns — 2 Years',
  required: true,
  status: 'uploaded'
}, {
  id: '6',
  name: 'Bank Statements — 2 Months',
  required: true,
  status: 'uploaded'
}, {
  id: '7',
  name: 'Asset / Investment Statements',
  required: true,
  status: 'uploaded'
}, {
  id: '8',
  name: 'Purchase Agreement / Sales Contract',
  required: true,
  status: 'uploaded'
}, {
  id: '9',
  name: 'Appraisal Report',
  required: true,
  status: 'uploaded'
}, {
  id: '10',
  name: 'Title Commitment',
  required: true,
  status: 'uploaded'
}, {
  id: '11',
  name: 'Homeowners Insurance Binder',
  required: true,
  status: 'uploaded'
}, {
  id: '12',
  name: 'Government-Issued Photo ID',
  required: true,
  status: 'uploaded'
}, {
  id: '13',
  name: 'Employment Verification (VOE)',
  required: true,
  status: 'uploaded'
}, {
  id: '14',
  name: 'AUS Findings (DU / LP)',
  required: true,
  status: 'uploaded'
}];
// ─── Form State ───────────────────────────────────────────────────────────────
interface FormData {
  loName: string;
  loNmls: string;
  loEmail: string;
  loPhone: string;
  loSpecialist: string;
  loBranch: string;
  borrowerFirst: string;
  borrowerLast: string;
  coBorrowerFirst: string;
  coBorrowerLast: string;
  borrowerDob: string;
  propAddress: string;
  propCity: string;
  propState: string;
  propZip: string;
  propType: string;
  occupancy: string;
  appraisedValue: string;
  salesPrice: string;
  loanAmount: string;
  loanType: string;
  loanPurpose: string;
  interestRate: string;
  loanTerm: string;
  ltv: string;
  cltv: string;
  dti: string;
  downPayment: string;
  closingDate: string;
  escrowCompany: string;
  lockExpiration: string;
  lenderName: string;
  productName: string;
  ausFinding: string;
  specialInstructions: string;
  submissionNotes: string;
  // IPAC Fields
  ipacBorrowerIncomeType: string;
  ipacBorrowerEmploymentStartDate: string;
  ipacBorrowerEmployer: string;
  ipacBorrowerTitle: string;
  ipacBorrowerAnnualIncome: string;
  ipacPropertyType: string;
  ipacAssetType: string;
  ipacAssetValue: string;
  ipacFicoScore: string;
  ipacPropertyTitleStatus: string;
  ipacSubjectPropertyAddress: string;
  ipacCoBorrowerIncomeType: string;
  ipacCoBorrowerAnnualIncome: string;
  ipacCoBorrowerEmploymentStartDate: string;
  ipacCoBorrowerEmployer: string;
  ipacCoBorrowerJobTitle: string;
  ipacNotes: string;
  ipacEmploymentAddress: string;
  // Loan Pricer Fields
  pricerApplicationDate: string;
  pricerChosenLender: string;
  pricerLoanChannel: string;
  pricerRefiOrPurchase: string;
  pricerPropertyType: string;
  pricerLoanPurpose: string;
  pricerLoanType: string;
  pricerProposedProgramType: string;
  pricerOccupancy: string;
  pricerProposedLoanAmount: string;
  pricerCashOutAmount: string;
  pricerProposedMonthlyPayment: string;
  pricerInterestRate: string;
  pricerAprQuote: string;
  pricerLtv: string;
  pricerPropertyValue: string;
  pricerTransactionType: string;
  pricerDownPayment: string;
  pricerSalesPrice: string;
}
const EMPTY_FORM: FormData = {
  loName: '',
  loNmls: '',
  loEmail: '',
  loPhone: '',
  loSpecialist: '',
  loBranch: '',
  borrowerFirst: '',
  borrowerLast: '',
  coBorrowerFirst: '',
  coBorrowerLast: '',
  borrowerDob: '',
  propAddress: '',
  propCity: '',
  propState: '',
  propZip: '',
  propType: '',
  occupancy: '',
  appraisedValue: '',
  salesPrice: '',
  loanAmount: '',
  loanType: '',
  loanPurpose: '',
  interestRate: '',
  loanTerm: '',
  ltv: '',
  cltv: '',
  dti: '',
  downPayment: '',
  closingDate: '',
  escrowCompany: '',
  lockExpiration: '',
  lenderName: '',
  productName: '',
  ausFinding: '',
  specialInstructions: '',
  submissionNotes: '',
  ipacBorrowerIncomeType: '',
  ipacBorrowerEmploymentStartDate: '',
  ipacBorrowerEmployer: '',
  ipacBorrowerTitle: '',
  ipacBorrowerAnnualIncome: '',
  ipacPropertyType: '',
  ipacAssetType: '',
  ipacAssetValue: '',
  ipacFicoScore: '',
  ipacPropertyTitleStatus: '',
  ipacSubjectPropertyAddress: '',
  ipacCoBorrowerIncomeType: '',
  ipacCoBorrowerAnnualIncome: '',
  ipacCoBorrowerEmploymentStartDate: '',
  ipacCoBorrowerEmployer: '',
  ipacCoBorrowerJobTitle: '',
  ipacNotes: '',
  ipacEmploymentAddress: '',
  pricerApplicationDate: '',
  pricerChosenLender: '',
  pricerLoanChannel: '',
  pricerRefiOrPurchase: '',
  pricerPropertyType: '',
  pricerLoanPurpose: '',
  pricerLoanType: '',
  pricerProposedProgramType: '',
  pricerOccupancy: '',
  pricerProposedLoanAmount: '',
  pricerCashOutAmount: '',
  pricerProposedMonthlyPayment: '',
  pricerInterestRate: '',
  pricerAprQuote: '',
  pricerLtv: '',
  pricerPropertyValue: '',
  pricerTransactionType: '',
  pricerDownPayment: '',
  pricerSalesPrice: ''
};
// ─── Shared Field Components ──────────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  required = false,
  prefix = '',
  suffix = '',
  hint = '',
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  prefix?: string;
  suffix?: string;
  hint?: string;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-1">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-2">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-3">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    }} data-id="element-4">
        {prefix && <span style={{
        position: 'absolute',
        left: '14px',
        color: c.textSecondary,
        fontSize: '14px',
        pointerEvents: 'none',
        zIndex: 1
      }} data-id="element-5">
            {prefix}
          </span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: `13px ${suffix ? '42px' : '16px'} 13px ${prefix ? '32px' : '16px'}`,
        fontSize: '14px',
        color: c.white,
        outline: 'none',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box',
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-6" />
        {suffix && <span style={{
        position: 'absolute',
        right: '14px',
        color: c.textSecondary,
        fontSize: '12px',
        pointerEvents: 'none',
        fontWeight: 600,
        letterSpacing: '0.5px'
      }} data-id="element-7">
            {suffix}
          </span>}
      </div>
      {hint && <span style={{
      fontSize: '10px',
      color: c.textMuted,
      letterSpacing: '0.5px'
    }} data-id="element-8">
          {hint}
        </span>}
    </div>;
}
function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  required?: boolean;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-9">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-10">
        {label}
        {required && <span style={{
        color: c.green,
        marginLeft: '3px'
      }} data-id="element-11">
            *
          </span>}
      </label>
      <div style={{
      position: 'relative'
    }} data-id="element-12">
        <select value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{
        width: '100%',
        backgroundColor: c.inputBg,
        border: `1px solid ${focused ? c.green : c.border}`,
        borderRadius: '6px',
        padding: '13px 40px 13px 16px',
        fontSize: '14px',
        color: value ? c.white : c.textSecondary,
        outline: 'none',
        appearance: 'none',
        cursor: 'pointer',
        boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxSizing: 'border-box'
      }} data-id="element-13">
          <option value="" style={{
          color: c.textSecondary
        }} data-id="element-14">
            Select…
          </option>
          {options.map(o => <option key={o.value} value={o.value} style={{
          backgroundColor: c.inputBg,
          color: c.white
        }} data-id="element-15">
              {o.label}
            </option>)}
        </select>
        <span style={{
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: c.textSecondary,
        fontSize: '9px',
        pointerEvents: 'none',
        fontWeight: 700
      }} data-id="element-16">
          ▼
        </span>
      </div>
    </div>;
}
function Textarea({
  label,
  value,
  onChange,
  placeholder = '',
  rows = 4,
  fullWidth = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-17">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: focused ? c.green : c.textLabel,
      transition: 'color 0.15s'
    }} data-id="element-18">
        {label}
      </label>
      <textarea value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} rows={rows} style={{
      width: '100%',
      backgroundColor: c.inputBg,
      border: `1px solid ${focused ? c.green : c.border}`,
      borderRadius: '6px',
      padding: '13px 16px',
      fontSize: '14px',
      color: c.white,
      outline: 'none',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxShadow: focused ? `0 0 0 1px ${c.green}` : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxSizing: 'border-box'
    }} data-id="element-19" />
    </div>;
}
function Checkbox({
  label,
  checked,
  onChange,
  fullWidth = false
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <label style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    gridColumn: fullWidth ? '1 / -1' : undefined,
    userSelect: 'none'
  }} data-id="element-20">
      <div style={{
      width: '18px',
      height: '18px',
      borderRadius: '4px',
      border: `1px solid ${checked ? c.green : c.border}`,
      backgroundColor: checked ? c.green : c.inputBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
      flexShrink: 0
    }} data-id="element-21">
        {checked && <span style={{
        color: '#0a0a0a',
        fontSize: '12px',
        fontWeight: 900
      }} data-id="element-22">
            ✓
          </span>}
      </div>
      <span style={{
      fontSize: '13px',
      color: c.white
    }} data-id="element-23">
        {label}
      </span>
    </label>;
}
function YesNoToggle({
  label,
  value,
  onChange,
  fullWidth = false
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  fullWidth?: boolean;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    gridColumn: fullWidth ? '1 / -1' : undefined
  }} data-id="element-24">
      <label style={{
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      color: c.textLabel
    }} data-id="element-25">
        {label}
      </label>
      <div style={{
      display: 'flex',
      gap: '8px'
    }} data-id="element-26">
        <button onClick={() => onChange(true)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === true ? c.green : c.border}`,
        backgroundColor: value === true ? c.greenFaint : c.inputBg,
        color: value === true ? c.green : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-27">
          Yes
        </button>
        <button onClick={() => onChange(false)} style={{
        flex: 1,
        padding: '10px',
        borderRadius: '6px',
        border: `1px solid ${value === false ? c.warning : c.border}`,
        backgroundColor: value === false ? `${c.warning}15` : c.inputBg,
        color: value === false ? c.warning : c.textSecondary,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-28">
          No
        </button>
      </div>
    </div>;
}
function SectionDivider({
  label
}: {
  label: string;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '36px 0 24px'
  }} data-id="element-29">
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-30" />
      <span style={{
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: c.green
    }} data-id="element-31">
        {label}
      </span>
      <div style={{
      flex: 1,
      height: '1px',
      backgroundColor: c.borderCard
    }} data-id="element-32" />
    </div>;
}
function SectionCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.03)',
    overflow: 'hidden',
    marginBottom: '20px'
  }} data-id="element-33">
      <div style={{
      borderTop: `3px solid ${c.green}`,
      padding: '18px 24px 14px',
      borderBottom: `1px solid ${c.border}`
    }} data-id="element-34">
        <h3 style={{
        margin: 0,
        fontSize: '14px',
        fontWeight: 700,
        color: c.white,
        letterSpacing: '-0.2px'
      }} data-id="element-35">
          {title}
        </h3>
      </div>
      <div style={{
      padding: '8px 24px 16px'
    }} data-id="element-36">
        {children}
      </div>
    </div>;
}
function FieldSection({
  children
}: {
  children: React.ReactNode;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    borderLeft: `3px solid ${c.green}`,
    paddingLeft: '28px'
  }} data-id="element-37">
      {children}
    </div>;
}
// ─── Step Content Components ──────────────────────────────────────────────────
function StepLoanOfficer({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-38">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-39">
        <Field label="Full Name" value={form.loName} onChange={v => set('loName', v)} required placeholder="Jane Smith" data-id="element-40" />
        <Field label="NMLS ID" value={form.loNmls} onChange={v => set('loNmls', v)} required placeholder="1234567" data-id="element-41" />
        <Field label="Email Address" value={form.loEmail} onChange={v => set('loEmail', v)} type="email" required placeholder="jane@example.com" data-id="element-42" />
        <Field label="Phone Number" value={form.loPhone} onChange={v => set('loPhone', v)} type="tel" placeholder="(844) 204-7863" data-id="element-43" />
        <Field label="Branch" value={form.loBranch} onChange={v => set('loBranch', v)} placeholder="Troy, MI" data-id="element-44" />
        <Select label="Submission Specialist" value={form.loSpecialist} onChange={v => set('loSpecialist', v)} required options={[{
        value: 'sarah',
        label: 'Sarah Johnson'
      }, {
        value: 'mike',
        label: 'Mike Chen'
      }, {
        value: 'lisa',
        label: 'Lisa Rodriguez'
      }, {
        value: 'david',
        label: 'David Kim'
      }, {
        value: 'amanda',
        label: 'Amanda Torres'
      }]} data-id="element-45" />
      </div>
    </FieldSection>;
}
function StepBorrower({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-46">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-47">
        <Field label="First Name" value={form.borrowerFirst} onChange={v => set('borrowerFirst', v)} required placeholder="John" data-id="element-48" />
        <Field label="Last Name" value={form.borrowerLast} onChange={v => set('borrowerLast', v)} required placeholder="Doe" data-id="element-49" />
        <Field label="Date of Birth" value={form.borrowerDob} onChange={v => set('borrowerDob', v)} type="date" required data-id="element-50" />
      </div>
      <SectionDivider label="Co-Borrower" data-id="element-51" />
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-52">
        <Field label="First Name" value={form.coBorrowerFirst} onChange={v => set('coBorrowerFirst', v)} placeholder="Jane" data-id="element-53" />
        <Field label="Last Name" value={form.coBorrowerLast} onChange={v => set('coBorrowerLast', v)} placeholder="Doe" data-id="element-54" />
      </div>
    </FieldSection>;
}
function StepProperty({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  return <FieldSection data-id="element-55">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-56">
        <Field label="Street Address" value={form.propAddress} onChange={v => set('propAddress', v)} required placeholder="123 Main Street" fullWidth data-id="element-57" />
        <Field label="City" value={form.propCity} onChange={v => set('propCity', v)} required placeholder="Troy" data-id="element-58" />
        <Select label="State" value={form.propState} onChange={v => set('propState', v)} required options={states.map(s => ({
        value: s,
        label: s
      }))} data-id="element-59" />
        <Field label="ZIP Code" value={form.propZip} onChange={v => set('propZip', v)} required placeholder="48083" data-id="element-60" />
        <Select label="Property Type" value={form.propType} onChange={v => set('propType', v)} required options={[{
        value: 'sfr',
        label: 'Single Family Residence'
      }, {
        value: 'condo',
        label: 'Condominium'
      }, {
        value: 'townhome',
        label: 'Townhome / PUD'
      }, {
        value: '2unit',
        label: '2-Unit'
      }, {
        value: '3unit',
        label: '3-Unit'
      }, {
        value: '4unit',
        label: '4-Unit'
      }, {
        value: 'mfh',
        label: 'Manufactured Home'
      }]} data-id="element-61" />
        <Select label="Occupancy" value={form.occupancy} onChange={v => set('occupancy', v)} required options={[{
        value: 'primary',
        label: 'Primary Residence'
      }, {
        value: 'second',
        label: 'Second Home'
      }, {
        value: 'investment',
        label: 'Investment Property'
      }]} data-id="element-62" />
        <Field label="Appraised Value" value={form.appraisedValue} onChange={v => set('appraisedValue', v)} required prefix="$" placeholder="350,000" data-id="element-63" />
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-64" />
      </div>
    </FieldSection>;
}
function StepLoanTerms({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-65">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-66">
        <Field label="Loan Amount" value={form.loanAmount} onChange={v => set('loanAmount', v)} required prefix="$" placeholder="310,000" data-id="element-67" />
        <Select label="Loan Type" value={form.loanType} onChange={v => set('loanType', v)} required options={[{
        value: 'conv',
        label: 'Conventional'
      }, {
        value: 'fha',
        label: 'FHA'
      }, {
        value: 'va',
        label: 'VA'
      }, {
        value: 'usda',
        label: 'USDA'
      }, {
        value: 'jumbo',
        label: 'Jumbo'
      }, {
        value: 'nonqm',
        label: 'Non-QM'
      }]} data-id="element-68" />
        <Select label="Loan Purpose" value={form.loanPurpose} onChange={v => set('loanPurpose', v)} required options={[{
        value: 'purchase',
        label: 'Purchase'
      }, {
        value: 'rr',
        label: 'Rate & Term Refinance'
      }, {
        value: 'cashout',
        label: 'Cash-Out Refinance'
      }, {
        value: 'streamline',
        label: 'Streamline Refinance'
      }]} data-id="element-69" />
        <Select label="Loan Term" value={form.loanTerm} onChange={v => set('loanTerm', v)} required options={[{
        value: '360',
        label: '30-Year Fixed'
      }, {
        value: '240',
        label: '20-Year Fixed'
      }, {
        value: '180',
        label: '15-Year Fixed'
      }, {
        value: '120',
        label: '10-Year Fixed'
      }, {
        value: '5_1arm',
        label: '5/1 ARM'
      }, {
        value: '7_1arm',
        label: '7/1 ARM'
      }, {
        value: '10_1arm',
        label: '10/1 ARM'
      }]} data-id="element-70" />
        <Field label="Interest Rate" value={form.interestRate} onChange={v => set('interestRate', v)} required suffix="%" placeholder="6.875" hint="Note rate, not APR" data-id="element-71" />
        <Field label="LTV" value={form.ltv} onChange={v => set('ltv', v)} required suffix="%" placeholder="88.57" data-id="element-72" />
        <Field label="CLTV" value={form.cltv} onChange={v => set('cltv', v)} suffix="%" placeholder="88.57" data-id="element-73" />
        <Field label="Back-End DTI" value={form.dti} onChange={v => set('dti', v)} required suffix="%" placeholder="43.5" data-id="element-74" />
      </div>
    </FieldSection>;
}
// ─── Loan Pricer Editable Row ─────────────────────────────────────────────────
function PricerRow({
  label,
  value,
  onChange,
  type = 'text',
  prefix = '',
  suffix = '',
  placeholder = '',
  options,
  hasInfo = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  options?: {
    value: string;
    label: string;
  }[];
  hasInfo?: boolean;
}) {
  const c = useContext(ThemeContext);
  const [focused, setFocused] = useState(false);
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${focused ? c.green + '40' : c.border}`,
    gap: '16px',
    minHeight: '48px',
    transition: 'border-color 0.15s'
  }} data-id="element-75">
      <div style={{
      width: '200px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }} data-id="element-76">
        <span style={{
        fontSize: '12px',
        color: focused ? c.green : c.textSecondary,
        letterSpacing: '0.1px',
        transition: 'color 0.15s'
      }} data-id="element-77">
          {label}
        </span>
        {hasInfo && <span style={{
        width: '15px',
        height: '15px',
        borderRadius: '50%',
        border: `1px solid ${c.textMuted}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '9px',
        color: c.textMuted,
        flexShrink: 0,
        cursor: 'help'
      }} data-id="element-78">
            i
          </span>}
      </div>
      <div style={{
      flex: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    }} data-id="element-79">
        {prefix && <span style={{
        position: 'absolute',
        left: '10px',
        fontSize: '14px',
        color: c.textSecondary,
        pointerEvents: 'none',
        zIndex: 1
      }} data-id="element-80">
            {prefix}
          </span>}
        {options ? <select value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{
        width: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '4px',
        padding: '8px 10px',
        fontSize: '14px',
        fontWeight: 500,
        color: value ? c.white : c.textMuted,
        outline: 'none',
        appearance: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.15s'
      }} data-id="element-81">
            <option value="" style={{
          backgroundColor: c.inputBg,
          color: c.textMuted
        }} data-id="element-82">
              Select…
            </option>
            {options.map(o => <option key={o.value} value={o.value} style={{
          backgroundColor: c.inputBg,
          color: c.white
        }} data-id="element-83">
                {o.label}
              </option>)}
          </select> : <input type={type} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} style={{
        width: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '4px',
        padding: `8px ${suffix ? '32px' : '10px'} 8px ${prefix ? '24px' : '10px'}`,
        fontSize: '14px',
        fontWeight: 500,
        color: c.white,
        outline: 'none',
        fontVariantNumeric: 'tabular-nums',
        transition: 'background-color 0.15s',
        boxSizing: 'border-box'
      }} data-id="element-84" />}
        {suffix && <span style={{
        position: 'absolute',
        right: '10px',
        fontSize: '12px',
        color: c.textSecondary,
        pointerEvents: 'none',
        fontWeight: 600
      }} data-id="element-85">
            {suffix}
          </span>}
      </div>
    </div>;
}
function StepLoanPricer({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  const c = useContext(ThemeContext);
  const isPurchase = form.pricerRefiOrPurchase === 'purchase';
  return <div data-id="element-86">
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0',
      marginBottom: '24px',
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${c.border}`,
      width: 'fit-content'
    }} data-id="element-87">
        <button onClick={() => set('pricerRefiOrPurchase', 'refinance')} style={{
        padding: '10px 28px',
        fontSize: '13px',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
        backgroundColor: !isPurchase ? c.green : c.sidebarBg,
        color: !isPurchase ? '#ffffff' : c.textSecondary,
        transition: 'all 0.15s',
        letterSpacing: '0.2px'
      }} data-id="element-88">
          Refinance
        </button>
        <button onClick={() => set('pricerRefiOrPurchase', 'purchase')} style={{
        padding: '10px 28px',
        fontSize: '13px',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
        backgroundColor: isPurchase ? c.green : c.sidebarBg,
        color: isPurchase ? '#ffffff' : c.textSecondary,
        transition: 'all 0.15s',
        letterSpacing: '0.2px'
      }} data-id="element-89">
          Purchase
        </button>
      </div>
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-90">
        {/* Left Column */}
        <div data-id="element-91">
          <SectionCard title="Loan Details" data-id="element-92">
            <PricerRow label="Application Date" value={form.pricerApplicationDate} onChange={v => set('pricerApplicationDate', v)} type="date" data-id="element-93" />
            <PricerRow label="Chosen Lender" value={form.pricerChosenLender} onChange={v => set('pricerChosenLender', v)} placeholder="" data-id="element-94" />
            <PricerRow label="Loan Channel" value={form.pricerLoanChannel} onChange={v => set('pricerLoanChannel', v)} options={[{
            value: 'correspondent',
            label: 'Correspondent'
          }, {
            value: 'wholesale',
            label: 'Wholesale'
          }, {
            value: 'retail',
            label: 'Retail'
          }, {
            value: 'broker',
            label: 'Broker'
          }]} data-id="element-95" />
            <PricerRow label="Refi or Purchase" value={form.pricerRefiOrPurchase} onChange={v => set('pricerRefiOrPurchase', v)} options={[{
            value: 'refinance',
            label: 'Refinance'
          }, {
            value: 'purchase',
            label: 'Purchase'
          }]} data-id="element-96" />
            <PricerRow label="Loan Type" value={form.pricerLoanType} onChange={v => set('pricerLoanType', v)} hasInfo options={[{
            value: 'conventional',
            label: 'Conventional'
          }, {
            value: 'fha',
            label: 'FHA'
          }, {
            value: 'va',
            label: 'VA'
          }, {
            value: 'usda',
            label: 'USDA'
          }, {
            value: 'jumbo',
            label: 'Jumbo'
          }, {
            value: 'nonqm',
            label: 'Non-QM'
          }]} data-id="element-97" />
            <PricerRow label="Loan Purpose" value={form.pricerLoanPurpose} onChange={v => set('pricerLoanPurpose', v)} options={[{
            value: 'refinance',
            label: 'Refinance'
          }, {
            value: 'purchase',
            label: 'Purchase'
          }, {
            value: 'cashout',
            label: 'Cash-Out Refinance'
          }, {
            value: 'streamline',
            label: 'Streamline Refinance'
          }]} data-id="element-98" />
            <PricerRow label="Proposed Program Type" value={form.pricerProposedProgramType} onChange={v => set('pricerProposedProgramType', v)} placeholder="" data-id="element-99" />
          </SectionCard>

          <SectionCard title="Property & Occupancy" data-id="element-100">
            <PricerRow label="Property Type" value={form.pricerPropertyType} onChange={v => set('pricerPropertyType', v)} options={[{
            value: 'single_family',
            label: 'Single-Family'
          }, {
            value: 'condo',
            label: 'Condominium'
          }, {
            value: 'townhome',
            label: 'Townhome / PUD'
          }, {
            value: '2unit',
            label: '2-Unit'
          }, {
            value: '3unit',
            label: '3-Unit'
          }, {
            value: '4unit',
            label: '4-Unit'
          }, {
            value: 'manufactured',
            label: 'Manufactured Home'
          }]} data-id="element-101" />
            <PricerRow label="Occupancy" value={form.pricerOccupancy} onChange={v => set('pricerOccupancy', v)} options={[{
            value: 'primary',
            label: 'Primary Residence'
          }, {
            value: 'second',
            label: 'Second Home'
          }, {
            value: 'investment',
            label: 'Investment Property'
          }]} data-id="element-102" />
          </SectionCard>
        </div>

        {/* Right Column */}
        <div data-id="element-103">
          <SectionCard title="Financial Summary" data-id="element-104">
            <PricerRow label="Proposed Loan Amount" value={form.pricerProposedLoanAmount} onChange={v => set('pricerProposedLoanAmount', v)} prefix="$" placeholder="" hasInfo data-id="element-105" />
            {isPurchase ? <>
                <PricerRow label="Sales Price" value={form.pricerSalesPrice} onChange={v => set('pricerSalesPrice', v)} prefix="$" placeholder="" data-id="element-106" />
                <PricerRow label="Down Payment" value={form.pricerDownPayment} onChange={v => set('pricerDownPayment', v)} prefix="$" placeholder="" data-id="element-107" />
              </> : <PricerRow label="Cash Out Amount" value={form.pricerCashOutAmount} onChange={v => set('pricerCashOutAmount', v)} prefix="$" placeholder="" data-id="element-108" />}
            <PricerRow label="Proposed Monthly Payment" value={form.pricerProposedMonthlyPayment} onChange={v => set('pricerProposedMonthlyPayment', v)} prefix="$" placeholder="" data-id="element-109" />
            <PricerRow label="Interest Rate" value={form.pricerInterestRate} onChange={v => set('pricerInterestRate', v)} suffix="%" placeholder="" data-id="element-110" />
            <PricerRow label="APR Quote" value={form.pricerAprQuote} onChange={v => set('pricerAprQuote', v)} suffix="%" placeholder="" data-id="element-111" />
            <PricerRow label="LTV" value={form.pricerLtv} onChange={v => set('pricerLtv', v)} suffix="%" placeholder="" data-id="element-112" />
            <PricerRow label="Property Value" value={form.pricerPropertyValue} onChange={v => set('pricerPropertyValue', v)} prefix="$" placeholder="" hasInfo data-id="element-113" />
            <PricerRow label="Transaction Type" value={form.pricerTransactionType} onChange={v => set('pricerTransactionType', v)} options={[{
            value: 'refinance',
            label: 'Refinance'
          }, {
            value: 'purchase',
            label: 'Purchase'
          }]} data-id="element-114" />
          </SectionCard>
        </div>
      </div>
    </div>;
}
function StepIPAC({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  const c = useContext(ThemeContext);
  return <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  }} data-id="element-115">
      {/* Left Column */}
      <div data-id="element-116">
        <SectionCard title="Borrower Information" data-id="element-117">
          <PricerRow label="Income Type" value={form.ipacBorrowerIncomeType} onChange={v => set('ipacBorrowerIncomeType', v)} options={[{
          value: 'w2',
          label: 'W-2'
        }, {
          value: 'self_employed',
          label: 'Self-Employed'
        }, {
          value: 'ssa',
          label: 'SSA / Disability'
        }, {
          value: 'pension',
          label: 'Pension'
        }, {
          value: 'rental',
          label: 'Rental Income'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-118" />
          <PricerRow label="Employment Start Date" value={form.ipacBorrowerEmploymentStartDate} onChange={v => set('ipacBorrowerEmploymentStartDate', v)} type="date" data-id="element-119" />
          <PricerRow label="Employer" value={form.ipacBorrowerEmployer} onChange={v => set('ipacBorrowerEmployer', v)} data-id="element-120" />
          <PricerRow label="Title" value={form.ipacBorrowerTitle} onChange={v => set('ipacBorrowerTitle', v)} data-id="element-121" />
          <PricerRow label="Annual Income" value={form.ipacBorrowerAnnualIncome} onChange={v => set('ipacBorrowerAnnualIncome', v)} prefix="$" data-id="element-122" />
        </SectionCard>

        <SectionCard title="Property & Assets" data-id="element-123">
          <PricerRow label="Property Type" value={form.ipacPropertyType} onChange={v => set('ipacPropertyType', v)} options={[{
          value: 'single_family',
          label: 'Single-Family'
        }, {
          value: 'condo',
          label: 'Condominium'
        }, {
          value: 'townhome',
          label: 'Townhome / PUD'
        }, {
          value: '2unit',
          label: '2-Unit'
        }, {
          value: '3unit',
          label: '3-Unit'
        }, {
          value: '4unit',
          label: '4-Unit'
        }, {
          value: 'manufactured',
          label: 'Manufactured Home'
        }]} data-id="element-124" />
          <PricerRow label="Asset Type" value={form.ipacAssetType} onChange={v => set('ipacAssetType', v)} options={[{
          value: 'checking',
          label: 'Checking Account'
        }, {
          value: 'savings',
          label: 'Savings Account'
        }, {
          value: 'retirement',
          label: 'Retirement Account'
        }, {
          value: 'investment',
          label: 'Investment Account'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-125" />
          <PricerRow label="Asset Value" value={form.ipacAssetValue} onChange={v => set('ipacAssetValue', v)} prefix="$" data-id="element-126" />
          <PricerRow label="FICO Score" value={form.ipacFicoScore} onChange={v => set('ipacFicoScore', v)} data-id="element-127" />
          <PricerRow label="Property Title Status" value={form.ipacPropertyTitleStatus} onChange={v => set('ipacPropertyTitleStatus', v)} options={[{
          value: 'clear',
          label: 'Clear'
        }, {
          value: 'liens',
          label: 'Has Liens'
        }, {
          value: 'pending',
          label: 'Pending'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-128" />
          <PricerRow label="Subject Property Address" value={form.ipacSubjectPropertyAddress} onChange={v => set('ipacSubjectPropertyAddress', v)} data-id="element-129" />
        </SectionCard>
      </div>

      {/* Right Column */}
      <div data-id="element-130">
        <SectionCard title="Co-Borrower Information" data-id="element-131">
          <PricerRow label="Income Type" value={form.ipacCoBorrowerIncomeType} onChange={v => set('ipacCoBorrowerIncomeType', v)} options={[{
          value: 'w2',
          label: 'W-2'
        }, {
          value: 'self_employed',
          label: 'Self-Employed'
        }, {
          value: 'ssa',
          label: 'SSA / Disability'
        }, {
          value: 'pension',
          label: 'Pension'
        }, {
          value: 'rental',
          label: 'Rental Income'
        }, {
          value: 'other',
          label: 'Other'
        }]} data-id="element-132" />
          <PricerRow label="Annual Income" value={form.ipacCoBorrowerAnnualIncome} onChange={v => set('ipacCoBorrowerAnnualIncome', v)} prefix="$" data-id="element-133" />
          <PricerRow label="Employment Start Date" value={form.ipacCoBorrowerEmploymentStartDate} onChange={v => set('ipacCoBorrowerEmploymentStartDate', v)} type="date" data-id="element-134" />
          <PricerRow label="Employer" value={form.ipacCoBorrowerEmployer} onChange={v => set('ipacCoBorrowerEmployer', v)} data-id="element-135" />
          <PricerRow label="Job Title" value={form.ipacCoBorrowerJobTitle} onChange={v => set('ipacCoBorrowerJobTitle', v)} data-id="element-136" />
        </SectionCard>

        <SectionCard title="Additional Details" data-id="element-137">
          <PricerRow label="I.P.A.C.T Notes" value={form.ipacNotes} onChange={v => set('ipacNotes', v)} data-id="element-138" />
          <PricerRow label="Employment Address" value={form.ipacEmploymentAddress} onChange={v => set('ipacEmploymentAddress', v)} data-id="element-139" />
        </SectionCard>
      </div>
    </div>;
}
function StepTransaction({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-140">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-141">
        <Field label="Sales Price" value={form.salesPrice} onChange={v => set('salesPrice', v)} required prefix="$" placeholder="345,000" data-id="element-142" />
        <Field label="Down Payment" value={form.downPayment} onChange={v => set('downPayment', v)} required prefix="$" placeholder="35,000" data-id="element-143" />
        <Field label="Target Closing Date" value={form.closingDate} onChange={v => set('closingDate', v)} type="date" required data-id="element-144" />
        <Field label="Lock Expiration Date" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-145" />
        <Field label="Escrow / Title Company" value={form.escrowCompany} onChange={v => set('escrowCompany', v)} placeholder="First American Title" fullWidth data-id="element-146" />
      </div>
    </FieldSection>;
}
function StepLender({
  form,
  set
}: {
  form: FormData;
  set: (k: keyof FormData, v: any) => void;
}) {
  return <FieldSection data-id="element-147">
      <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    }} data-id="element-148">
        <Field label="Lender / Investor" value={form.lenderName} onChange={v => set('lenderName', v)} required placeholder="United Wholesale Mortgage" data-id="element-149" />
        <Field label="Product / Program" value={form.productName} onChange={v => set('productName', v)} required placeholder="Conv 30yr Fixed" data-id="element-150" />
        <Select label="AUS Finding" value={form.ausFinding} onChange={v => set('ausFinding', v)} required options={[{
        value: 'approve_eligible',
        label: 'Approve / Eligible (DU)'
      }, {
        value: 'accept',
        label: 'Accept (LP)'
      }, {
        value: 'refer_eligible',
        label: 'Refer / Eligible'
      }, {
        value: 'refer_caution',
        label: 'Refer with Caution'
      }, {
        value: 'manual',
        label: 'Manual Underwrite'
      }]} data-id="element-151" />
        <Field label="Rate Lock Expiration" value={form.lockExpiration} onChange={v => set('lockExpiration', v)} type="date" data-id="element-152" />
        <Textarea label="Special Instructions" value={form.specialInstructions} onChange={v => set('specialInstructions', v)} placeholder="Rush request, lender-specific notes, exceptions..." rows={3} fullWidth data-id="element-153" />
        <Textarea label="Submission Notes" value={form.submissionNotes} onChange={v => set('submissionNotes', v)} placeholder="Notes for the submission specialist…" rows={3} fullWidth data-id="element-154" />
      </div>
    </FieldSection>;
}
// ─── Condition Row ────────────────────────────────────────────────────────────
function ConditionRow({
  condition
}: {
  condition: Condition;
}) {
  const c = useContext(ThemeContext);
  const isUploaded = condition.status === 'uploaded';
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px 12px 0',
    borderRadius: '4px',
    border: `1px solid ${isUploaded ? `${c.green}33` : `${c.warning}33`}`,
    backgroundColor: isUploaded ? c.greenFaint : c.deepCard,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: '6px'
  }} data-id="element-155">
      <div style={{
      width: '3px',
      alignSelf: 'stretch',
      backgroundColor: isUploaded ? c.green : c.warning,
      flexShrink: 0,
      borderRadius: '0 2px 2px 0'
    }} data-id="element-156" />
      <span style={{
      flex: 1,
      fontSize: '13px',
      color: c.white,
      letterSpacing: '0.1px'
    }} data-id="element-157">
        {condition.name}
      </span>
      <span style={{
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
      padding: '3px 10px',
      borderRadius: '2px',
      flexShrink: 0,
      marginRight: '4px',
      backgroundColor: isUploaded ? `${c.green}20` : `${c.warning}20`,
      color: isUploaded ? c.green : c.warning,
      border: `1px solid ${isUploaded ? `${c.green}40` : `${c.warning}40`}`
    }} data-id="element-158">
        {isUploaded ? 'Uploaded' : 'Not Uploaded'}
      </span>
    </div>;
}
// ─── Conditions Step ──────────────────────────────────────────────────────────
function StepConditions({
  conditions,
  uploadedCount,
  requiredCount,
  allUploaded
}: {
  conditions: Condition[];
  uploadedCount: number;
  requiredCount: number;
  allUploaded: boolean;
}) {
  const c = useContext(ThemeContext);
  const pendingCount = requiredCount - uploadedCount;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
  }} data-id="element-159">
      {/* Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px'
    }} data-id="element-160">
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-161">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: c.green,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-162">
            {uploadedCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-163">
            Uploaded
          </div>
        </div>
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-164">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: pendingCount > 0 ? c.warning : c.green,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-165">
            {pendingCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-166">
            Not Uploaded
          </div>
        </div>
        <div style={{
        backgroundColor: c.deepCard,
        border: `1px solid ${c.borderCard}`,
        borderRadius: '4px',
        padding: '16px 18px'
      }} data-id="element-167">
          <div style={{
          fontSize: '28px',
          fontWeight: 800,
          color: c.textSecondary,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1
        }} data-id="element-168">
            {requiredCount}
          </div>
          <div style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: c.textLabel,
          marginTop: '6px'
        }} data-id="element-169">
            Total Required
          </div>
        </div>
      </div>

      {/* Progress */}
      <div data-id="element-170">
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }} data-id="element-171">
          <span style={{
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: c.textLabel
        }} data-id="element-172">
            Upload Progress
          </span>
          <span style={{
          fontSize: '11px',
          fontWeight: 800,
          color: allUploaded ? c.green : c.warning,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-173">
            {Math.round(uploadedCount / requiredCount * 100)}%
          </span>
        </div>
        <div style={{
        height: '2px',
        backgroundColor: c.border,
        borderRadius: '1px',
        overflow: 'hidden'
      }} data-id="element-174">
          <div style={{
          height: '100%',
          width: `${uploadedCount / requiredCount * 100}%`,
          backgroundColor: allUploaded ? c.green : c.warning,
          transition: 'width 0.4s ease'
        }} data-id="element-175" />
        </div>
      </div>

      {/* Status banner */}
      {!allUploaded && <div style={{
      padding: '12px 16px',
      backgroundColor: `${c.warning}10`,
      border: `1px solid ${c.warning}33`,
      borderLeft: `3px solid ${c.warning}`,
      borderRadius: '0 4px 4px 0'
    }} data-id="element-176">
          <p style={{
        fontSize: '12px',
        fontWeight: 700,
        color: c.warning,
        margin: '0 0 3px'
      }} data-id="element-177">
            {pendingCount} document{pendingCount !== 1 ? 's' : ''} not yet
            uploaded
          </p>
          <p style={{
        fontSize: '11px',
        color: c.textSecondary,
        margin: 0,
        lineHeight: '1.5'
      }} data-id="element-178">
            All documents must be uploaded in the Document Management system
            before you can proceed.
          </p>
        </div>}
      {allUploaded && <div style={{
      padding: '12px 16px',
      backgroundColor: c.greenFaint,
      border: `1px solid ${c.green}33`,
      borderLeft: `3px solid ${c.green}`,
      borderRadius: '0 4px 4px 0'
    }} data-id="element-179">
          <p style={{
        fontSize: '12px',
        fontWeight: 700,
        color: c.green,
        margin: '0 0 3px'
      }} data-id="element-180">
            All documents uploaded
          </p>
          <p style={{
        fontSize: '11px',
        color: c.textSecondary,
        margin: 0
      }} data-id="element-181">
            You may proceed with the submission.
          </p>
        </div>}

      {/* Document list */}
      <div data-id="element-182">
        <div style={{
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        color: c.textLabel,
        marginBottom: '12px'
      }} data-id="element-183">
          Required Documents
        </div>
        {conditions.map(cond => <ConditionRow key={cond.id} condition={cond} data-id="element-184" />)}
      </div>
    </div>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function LoanSubmissionForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(() => {
    try {
      const saved = localStorage.getItem('loan_form_data');
      if (saved) return {
        ...EMPTY_FORM,
        ...JSON.parse(saved)
      };
    } catch {}
    return EMPTY_FORM;
  });
  const [submitted, setSubmitted] = useState(false);
  const [saved, setSaved] = useState(false);
  const c = LIGHT_THEME;
  const setField = (key: keyof FormData, value: any) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  // Validation: check all Loan Pricer + I.P.A.C.T fields are filled
  const isPurchase = form.pricerRefiOrPurchase === 'purchase';
  const pricerFields: (keyof FormData)[] = ['pricerApplicationDate', 'pricerChosenLender', 'pricerLoanChannel', 'pricerRefiOrPurchase', 'pricerPropertyType', 'pricerLoanPurpose', 'pricerLoanType', 'pricerProposedProgramType', 'pricerOccupancy', 'pricerProposedLoanAmount', ...(isPurchase ? ['pricerSalesPrice', 'pricerDownPayment'] as (keyof FormData)[] : ['pricerCashOutAmount'] as (keyof FormData)[]), 'pricerProposedMonthlyPayment', 'pricerInterestRate', 'pricerAprQuote', 'pricerLtv', 'pricerPropertyValue', 'pricerTransactionType'];
  const requiredFields: (keyof FormData)[] = [...pricerFields, 'ipacBorrowerIncomeType', 'ipacBorrowerEmploymentStartDate', 'ipacBorrowerEmployer', 'ipacBorrowerTitle', 'ipacBorrowerAnnualIncome', 'ipacPropertyType', 'ipacAssetType', 'ipacAssetValue', 'ipacFicoScore', 'ipacPropertyTitleStatus', 'ipacSubjectPropertyAddress', 'ipacCoBorrowerIncomeType', 'ipacCoBorrowerAnnualIncome', 'ipacCoBorrowerEmploymentStartDate', 'ipacCoBorrowerEmployer', 'ipacCoBorrowerJobTitle', 'ipacNotes', 'ipacEmploymentAddress'];
  const filledCount = requiredFields.filter(k => {
    const v = form[k];
    return typeof v === 'string' ? v.trim() !== '' : !!v;
  }).length;
  const allFilled = filledCount === requiredFields.length;
  const remainingCount = requiredFields.length - filledCount;
  const handleSave = () => {
    try {
      localStorage.setItem('loan_form_data', JSON.stringify(form));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };
  const handleSubmit = () => {
    if (!allFilled) return;
    handleSave();
    setSubmitted(true);
  };
  const stepTitles: Record<number, {
    title: string;
    subtitle: string;
  }> = {
    1: {
      title: 'Loan Pricer',
      subtitle: 'Loan pricing details and program selection'
    },
    2: {
      title: 'I.P.A.C.T',
      subtitle: 'Loan File Verification'
    }
  };
  return <ThemeContext.Provider value={c} data-id="element-185">
      <div style={{
      display: 'flex',
      height: '100vh',
      backgroundColor: c.pageBg,
      fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
      overflow: 'hidden',
      transition: 'background-color 0.2s'
    }} data-id="element-186">
        {/* ── SIDEBAR ────────────────────────────────────────────────────── */}
        <aside style={{
        width: '268px',
        flexShrink: 0,
        backgroundColor: c.sidebarBg,
        borderRight: `1px solid ${c.border}`,
        borderLeft: 'none',
        display: 'flex',
        flexDirection: 'column',
        padding: '36px 0',
        overflowY: 'auto',
        transition: 'background-color 0.2s'
      }} data-id="element-187">
          {/* Logo */}
          <div style={{
          padding: '0 28px 24px'
        }} data-id="element-188">
            <img src="https://cdn.magicpatterns.com/uploads/ivTKqNfSAnARDkCbGSzVuW/logo_transparent.png" alt="Neighborhood Mortgage Company" style={{
            width: '180px',
            height: 'auto',
            display: 'block'
          }} data-id="element-189" />
          </div>

          {/* Tab Navigator */}
          <nav style={{
          flex: 1,
          padding: '20px 0'
        }} data-id="element-190">
            {STEPS.map(s => {
            const isActive = step === s.id;
            return <button key={s.id} onClick={() => setStep(s.id)} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '14px 28px',
              border: 'none',
              borderLeft: `3px solid ${isActive ? c.green : 'transparent'}`,
              backgroundColor: isActive ? c.greenFaint : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.15s',
              textAlign: 'left'
            }} data-id="element-191">
                  <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: isActive ? c.green : c.textMuted,
                flexShrink: 0,
                transition: 'background-color 0.15s'
              }} data-id="element-192" />
                  <span style={{
                fontSize: '13px',
                fontWeight: isActive ? 700 : 400,
                color: isActive ? c.white : c.textSecondary,
                transition: 'color 0.15s'
              }} data-id="element-193">
                    {s.label}
                  </span>
                </button>;
          })}
          </nav>

          {/* Footer */}
          <div style={{
          padding: '20px 28px 0',
          marginTop: '16px'
        }} data-id="element-194">
            <p style={{
            fontSize: '10px',
            color: c.textMuted,
            lineHeight: '1.7',
            letterSpacing: '0.2px'
          }} data-id="element-195">
              Neighborhood Mortgage Company
            </p>
          </div>
        </aside>

        {/* ── CONTENT AREA ───────────────────────────────────────────────── */}
        <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: c.pageBg,
        transition: 'background-color 0.2s'
      }} data-id="element-196">
          {/* Header */}
          <div style={{
          padding: '32px 56px 28px',
          borderBottom: `1px solid ${c.border}`,
          flexShrink: 0
        }} data-id="element-197">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto'
          }} data-id="element-198">
              <h1 style={{
              margin: 0,
              fontSize: '40px',
              fontWeight: 800,
              color: c.white,
              letterSpacing: '-1.5px',
              lineHeight: 1.1
            }} data-id="element-199">
                {stepTitles[step].title}
              </h1>
              <p style={{
              margin: '6px 0 0',
              fontSize: '14px',
              color: c.textSecondary,
              letterSpacing: '0.1px'
            }} data-id="element-200">
                {stepTitles[step].subtitle}
              </p>
            </div>
          </div>

          {/* Scrollable form body */}
          <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '48px 56px'
        }} data-id="element-201">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%'
          }} data-id="element-202">
              {step === 1 && <StepLoanPricer form={form} set={setField} data-id="element-203" />}
              {step === 2 && <StepIPAC form={form} set={setField} data-id="element-204" />}
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{
          padding: '18px 56px',
          borderTop: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: c.sidebarBg,
          transition: 'background-color 0.2s'
        }} data-id="element-205">
            <div style={{
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }} data-id="element-206">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }} data-id="element-207">
                <button onClick={handleSave} style={{
                padding: '11px 28px',
                borderRadius: '10px',
                border: `1px solid ${c.border}`,
                backgroundColor: saved ? c.greenFaint : c.sidebarBg,
                color: saved ? c.green : c.textSecondary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.3px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }} data-id="element-208">
                  {saved ? '✓ Saved' : 'Save Draft'}
                </button>
                <button onClick={handleSubmit} disabled={submitted || !allFilled} title={!allFilled ? 'All fields must be filled before converting' : undefined} style={{
                padding: '11px 32px',
                borderRadius: '10px',
                border: !allFilled && !submitted ? `1px solid ${c.border}` : 'none',
                backgroundColor: submitted ? c.deepCard : allFilled ? c.green : c.deepCard,
                color: submitted ? c.textMuted : allFilled ? '#ffffff' : c.textMuted,
                fontSize: '14px',
                fontWeight: 700,
                cursor: submitted || !allFilled ? 'not-allowed' : 'pointer',
                letterSpacing: '0.3px',
                transition: 'all 0.2s',
                boxShadow: allFilled && !submitted ? `0 2px 12px ${c.green}40` : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: !allFilled && !submitted ? 0.7 : 1
              }} data-id="element-209">
                  {submitted ? '✓ Converted' : 'Convert'}
                </button>
              </div>
            </div>
          </div>

          {submitted && <div style={{
          padding: '14px 56px',
          backgroundColor: c.greenFaint,
          borderTop: `1px solid ${c.green}33`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }} data-id="element-210">
              <span style={{
            fontSize: '16px'
          }} data-id="element-211">
                ✅
              </span>
              <div data-id="element-212">
                <p style={{
              fontSize: '12px',
              fontWeight: 700,
              color: c.green,
              margin: 0,
              letterSpacing: '0.5px'
            }} data-id="element-213">
                  CONVERSION SUCCESSFUL
                </p>
                <p style={{
              fontSize: '12px',
              color: c.textSecondary,
              margin: '2px 0 0'
            }} data-id="element-214">
                  Loan has been successfully converted and queued for
                  underwriting.
                </p>
              </div>
            </div>}
        </main>
      </div>
    </ThemeContext.Provider>;
}